import React, { useEffect, useRef } from "react";
import ChatMessage from "./components/ChatMessage";
import AttachIcon from "./components/AttachIcon";
import SendIcon from "./components/SendIcon";
import { useClientChat } from "./hooks/useClientChat";
import chatBackgroundUrl from "../assets/chatbackground.png";
import styles from "./ClientChatPage.module.css";

export interface ClientChatPageProps {
  /** UUID клиента из маршрута /chat/:uuid */
  uuid: string;
}

const ClientChatPage: React.FC<ClientChatPageProps> = ({ uuid }) => {
  const {
    messages,
    messageText,
    setMessageText,
    isReady,
    isSending,
    isConnecting,
    error,
    sendMessage,
  } = useClientChat(uuid);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const canSend = isReady && !isSending && messageText.trim().length > 0;
  const showCenteredOverlay =
    messages.length === 0 && (isConnecting || Boolean(error));
  const showEmptyState = messages.length === 0 && !isConnecting && !error;

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    sendMessage();
  };

  return (
    <div className={styles.page}>
      <main className={styles.main}>
        <div
          className={styles.messagesArea}
          style={{ backgroundImage: `url(${chatBackgroundUrl})` }}
        >
          <div className={styles.messagesScroll}>
            {messages.map((message) => (
              <ChatMessage
                key={message.id}
                text={message.text}
                sender={message.sender}
                timeLabel={message.timeLabel}
                isOutgoing={message.isOutgoing}
              />
            ))}
            <div ref={messagesEndRef} />
          </div>

          {showCenteredOverlay ? (
            <div className={styles.centerOverlay}>
              {isConnecting ? (
                <p className={styles.state}>Подключение…</p>
              ) : error ? (
                <p className={styles.serviceNotice}>{error}</p>
              ) : null}
            </div>
          ) : null}

          {showEmptyState ? (
            <div className={styles.centerOverlay}>
              <p className={styles.state}>Сообщений пока нет</p>
            </div>
          ) : null}
        </div>

        <form className={styles.sendForm} onSubmit={onSubmit}>
          <div className={styles.sendPrimaryRow}>
            <button
              type="button"
              className={styles.attachButton}
              disabled={!isReady}
              aria-label="Прикрепить файл"
              title="Прикрепить файл"
            >
              <AttachIcon />
            </button>
            <input
              className={styles.sendInput}
              value={messageText}
              onChange={(event) => setMessageText(event.target.value)}
              placeholder="Текст сообщения"
              disabled={!isReady || isSending}
              autoComplete="off"
            />
            <button
              type="submit"
              className={styles.sendButton}
              disabled={!canSend}
              aria-label="Отправить"
              title="Отправить"
            >
              <SendIcon />
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default ClientChatPage;
